import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    PLATFORM_ID,
    ViewEncapsulation,
} from '@angular/core';
import { InjectedScript, InjectionOptions, injectScript, injectStyle } from '@hyundai/ng-common-lib';
import { IttCartItem } from '@hyundai/shared-lib';
import { Store } from '@ngrx/store';

declare global {
    interface Window {
        renderHpm: () => void;
    }
}

@Component({
    selector: 'lib-vehicle-equipment',
    standalone: true,
    imports: [],
    templateUrl: './vehicle-equipment.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class VehicleEquipmentComponent implements OnDestroy, AfterViewInit {
    protected readonly document = inject(DOCUMENT);
    store = inject(Store);
    dealerId = input.required<string>();
    series = input.required<string>();
    modelyear = input<number>();
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    // Added because of pm strategy decicions, would be removed if customer decides to use the itt model check
    deactivateModelCheckTemporary = true;

    ittVehicleTree = [
        'ACCENT ab 2000',
        'ACCENT ab 2006',
        'ACCENT FL ab 2003',
        'ATOS ab 2000',
        'ATOS ab 2004',
        'ATOS ab 2004',
        'BAYON ab 2021',
        'BAYON FL ab 2024',
        'COUPE 2002-2009',
        'ELANTRA ab 2000',
        'ELANTRA ab 2003',
        'GENESIS',
        'GETZ ab 2002',
        'GETZ FL ab 2006',
        'GRAND SANTA FE ab 2013',
        'GRAND SANTA FE ab 2016',
        'GRANDEUR',
        'H-1 Bus ab 1999',
        'H-1 Bus ab 2006',
        'H-1 Bus ab 2008',
        'H-1 Bus ab 2018',
        'H-1 Bus FL ab 2015',
        'H-1 Transporter 2008 bis 2020',
        'H-1 Transporter 2088 bis 2020',
        'H-1 Transporter ab 1997',
        'H-1 Transporter ab 2008',
        'H-1 Transporter ab 2015',
        'H-1 Transporter ab 2018',
        'H350 CARGO ab 2015',
        'i10 ab 2008',
        'i10 ab 2013',
        'i10 ab 2019',
        'i10 FL ab 2010',
        'i10 FL ab 2016',
        'i20 ab 2009',
        'i20 ab 2014',
        'i20 ab 2020',
        'i20 FL ab 2012',
        'i20 FL ab 2018',
        'i20 N ab 2021',
        'i30 2007-2012',
        'i30 2011-2017',
        'i30 ab 2007',
        'i30 ab 2011',
        'i30 ab 2017',
        'i30 FL 2007-2012',
        'i30 FL ab 2010',
        'i30 FL ab 2015',
        'i30 FL ab 2020',
        'i30 FL ab 2024',
        'i30 N ab 2017',
        'i30 N ab 2021',
        'i40 ab 2011',
        'i40 FL ab 2015',
        'i40 FL ab 2018',
        'IONIQ ab 2016',
        'IONIQ FL ab 2019',
        'IONIQ 5 ab 2021',
        'IONIQ 5 N ab 2024',
        'IONIQ 5 FL ab 2024',
        'IONIQ 6 ab 2021',
        'IONIQ 6 ab 2022',
        'ix20 ab 2010',
        'ix20 ab 2015',
        'ix35 ab 2010',
        'ix35 FL ab 2013',
        'ix35 Fuel Cell',
        'ix55',
        'KONA ab 2023',
        'KONA ab 2017',
        'KONA FL ab 2020',
        'KONA ab 2020',
        'KONA N ab 2021',
        'MATRIX ab 2001',
        'MATRIX ab 2005',
        'MATRIX ab 2008',
        'NEXO ab 2018',
        'SANTA FE ab 2000',
        'SANTA FE ab 2006',
        'SANTA FE ab 2012',
        'SANTA FE ab 2018',
        'SANTA FE ab 2023',
        'SANTA FE FL ab 2004',
        'SANTA FE FL ab 2009',
        'SANTA FE FL ab 2016',
        'SANTA FE FL ab 2020',
        'SANTA FE ab 2024',
        'SONATA ab 1999',
        'SONATA ab 2004',
        'SONATA FL ab 2003',
        'SONATA FL ab 2008',
        'SONATA LANTRA',
        'STARIA ab 2021',
        'TERRACAN ab 2001',
        'TERRACAN ab 2004',
        'TRAJET ab 2000',
        'TRAJET ab 2004',
        'TUCSON ab 2004',
        'TUCSON ab 2015',
        'TUCSON ab 2020',
        'TUCSON ab 2024',
        'TUCSON FL ab 2018',
        'TUCSON FL ab 2024',
        'VELOSTER ab 2011',
        'VELOSTER FL ab 2015',
        'XG30',
    ];
    model = computed(() => {
        return this.deactivateModelCheckTemporary
            ? null
            : this.ittVehicleTree.find(
                  (item) => item.toLowerCase() === `${this.series()} ab ${this.modelyear()}`.toLowerCase(),
              );
    });

    dummyPartnerIds: string[] = ['1001', '1338', '1339'];
    scripts: InjectedScript[] = [];

    shopIdentifier = computed(
        () => `hyundai_${this.dummyPartnerIds.includes(this.dealerId()) ? '2452' : this.dealerId()}`,
    );

    constructor() {
        this.loadScripts();
    }

    ngAfterViewInit() {
        if (this.isBrowser) {
            const element = this.document.querySelector('#itt-sl-ucl');
            if (element) {
                element.addEventListener('CART_CHANGE', (e) => {
                    const cart: IttCartItem[] = (e as CustomEvent).detail;

                    if (cart) {
                        // save cart in session storage
                        sessionStorage.setItem(
                            'cart',
                            JSON.stringify(
                                cart.map((item: IttCartItem) => ({
                                    price: item.price,
                                    name: item.name,
                                    articleNumber: item.articleNumber,
                                })),
                            ),
                        );
                    }
                });
            } else {
                console.error('Element not found');
            }
        }
    }

    async loadScripts() {
        const options: InjectionOptions = {
            reuse: true,
        };
        this.scripts = await Promise.all([
            injectStyle('https://selekt.sl-gcp.itt-dev.de/static/css/main.css', options),
            //injectScript('https://selekt.sl-gcp.itt-dev.de/static/js/runtime-main.js', options),
            injectScript('https://selekt.sl-gcp.itt-dev.de/static/js/main.js', options),
        ]);
    }

    ngOnDestroy(): void {
        for (const script of this.scripts) {
            if (script) {
                script.remove();
            }
        }
    }
}
